import { createInputBus, type InputBus } from './input';
import { createTimer, type Timer } from './timer';
import { createBoopTarget } from './targets/boopTarget';
import type { InputEvent, Target } from './target';
import type { AudioBus, GameView } from './gameView';
import type { Settings } from '../shared/settings';
import {
  COMBO_AMP_THRESHOLD,
  DIFFICULTY_PROFILES,
  KEY_TARGETS,
  POINTS_PENALTY_TIMEOUT,
  POINTS_PENALTY_WRONG,
  PRAISE_WORDS,
  SCORE_BASE,
  SCORE_COMBO_CAP,
  SCORE_COMBO_STEP,
  TARGET_MIN_SEPARATION_REM,
  TARGET_SPAWN_MARGIN_REM,
  TIME_PENALTY_TIMEOUT_S,
  TIME_TICK_THRESHOLD_S,
} from '../shared/config';
import { remToPx } from '../shared/scale';

export interface EngineStats {
  score: number;
  hits: number;
  misses: number;
  bestCombo: number;
}

export interface EngineOptions {
  playArea: HTMLElement;
  view: GameView;
  audio: AudioBus;
  settings: Settings;
  onEnd: (stats: EngineStats) => void;
}

export interface Engine {
  start(): void;
  pause(): void;
  resume(): void;
  stop(): void;
  getStats(): EngineStats;
  getCombo(): number;
  getRemaining(): number;
  isPaused(): boolean;
  adjustTimer(deltaSeconds: number): void;
  endNow(): void;
}

export function createEngine({
  playArea,
  view,
  audio,
  settings,
  onEnd,
}: EngineOptions): Engine {
  const profile = DIFFICULTY_PROFILES[settings.difficulty];
  const inputBus: InputBus = createInputBus(playArea);

  const stats: EngineStats = { score: 0, hits: 0, misses: 0, bestCombo: 0 };
  let combo = 0;
  let targets: Target[] = [];
  let nextId = 1;
  let spawnIntervalId: number | null = null;
  let rafId: number | null = null;
  let stopped = false;
  let paused = false;
  let pausedAt: number | null = null;
  let totalPausedMs = 0;
  let timer: Timer | null = null;

  function clock(): number {
    if (paused && pausedAt !== null) {
      return pausedAt - totalPausedMs;
    }
    return performance.now() - totalPausedMs;
  }

  function publishHud(): void {
    view.setScore(stats.score);
    view.setCombo(combo);
  }

  function colorVar(colorKey: string): string {
    return `var(--${colorKey})`;
  }

  function pickPraise(): string {
    return PRAISE_WORDS[Math.floor(Math.random() * PRAISE_WORDS.length)] ?? 'BOOP!';
  }

  function tooClose(x: number, y: number): boolean {
    const minSeparation = remToPx(TARGET_MIN_SEPARATION_REM);
    return targets.some((t) => {
      const dx = t.x - x;
      const dy = t.y - y;
      return Math.hypot(dx, dy) < minSeparation;
    });
  }

  function trySpawn(): void {
    if (stopped || paused) return;
    if (targets.length >= profile.maxCircles) return;
    const rect = playArea.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const margin = remToPx(TARGET_SPAWN_MARGIN_REM);
    const minX = margin;
    const maxX = rect.width - margin;
    const minY = margin;
    const maxY = rect.height - margin;
    if (maxX <= minX || maxY <= minY) return;

    const x = minX + Math.random() * (maxX - minX);
    const y = minY + Math.random() * (maxY - minY);
    if (tooClose(x, y)) return;

    const id = nextId++;
    const target = createBoopTarget({
      id,
      x,
      y,
      lifetimeMs: profile.lifetimeMs,
      initialColorIndex: id,
      clock,
    });
    target.render(playArea);
    targets.push(target);
  }

  function applyTimeoutPenalty(count: number): void {
    if (count <= 0) return;
    if (settings.failMode === 'points') {
      stats.score = Math.max(0, stats.score - POINTS_PENALTY_TIMEOUT * count);
    } else if (settings.failMode === 'time') {
      timer?.adjust(-TIME_PENALTY_TIMEOUT_S * count);
    }
  }

  function checkExpired(now: number): void {
    let timedOut = 0;
    const survivors: Target[] = [];
    for (const t of targets) {
      if (t.isExpired(now)) {
        t.destroy();
        timedOut += 1;
      } else {
        survivors.push(t);
      }
    }
    if (timedOut > 0) {
      targets = survivors;
      stats.misses += timedOut;
      combo = 0;
      applyTimeoutPenalty(timedOut);
      view.setMood(settings.failMode === 'none' ? 'happy' : 'sad');
      publishHud();
    }
  }

  function frame(): void {
    if (stopped || paused) {
      rafId = null;
      return;
    }
    const now = clock();
    for (const t of targets) {
      t.tick(now);
    }
    checkExpired(now);
    rafId = window.requestAnimationFrame(frame);
  }

  function startFrameLoop(): void {
    if (rafId !== null) return;
    rafId = window.requestAnimationFrame(frame);
  }

  function stopFrameLoop(): void {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  }

  function startSpawnLoop(): void {
    if (spawnIntervalId !== null) return;
    spawnIntervalId = window.setInterval(trySpawn, profile.spawnEveryMs);
  }

  function stopSpawnLoop(): void {
    if (spawnIntervalId !== null) {
      clearInterval(spawnIntervalId);
      spawnIntervalId = null;
    }
  }

  function handleClick(event: InputEvent): void {
    for (const t of targets) {
      if (t.state !== 'circle') continue;
      const result = t.matches(event);
      if (result === 'progress') {
        audio.click();
        view.spawnBurst(t.x, t.y, colorVar(t.colorKey));
        return;
      }
    }
  }

  function handleKey(event: InputEvent & { type: 'key' }): void {
    if (!KEY_TARGETS.includes(event.key as (typeof KEY_TARGETS)[number])) return;

    const candidates = targets.filter(
      (t) => t.state === 'letter' && t.key === event.key,
    );
    if (candidates.length > 0) {
      // pick the oldest waiting letter (lowest id, since ids increase over time)
      const matched = candidates.reduce((a, b) => (a.id < b.id ? a : b));
      const result = matched.matches(event);
      if (result === 'complete') {
        targets = targets.filter((t) => t.id !== matched.id);
        matched.destroy();
        audio.correct();
        combo += 1;
        if (combo > stats.bestCombo) stats.bestCombo = combo;
        if (combo >= COMBO_AMP_THRESHOLD) audio.combo(combo);
        const points = SCORE_BASE + Math.min(combo, SCORE_COMBO_CAP) * SCORE_COMBO_STEP;
        stats.score += points;
        stats.hits += 1;
        view.spawnFloater(matched.x, matched.y, pickPraise(), colorVar(matched.colorKey));
        view.spawnBurst(matched.x, matched.y, colorVar(matched.colorKey));
        view.setMood(combo >= COMBO_AMP_THRESHOLD ? 'cheer' : 'happy');
        publishHud();
        return;
      }
    }

    // wrong key — no letter target was waiting for it
    audio.wrong();
    combo = 0;
    if (settings.failMode === 'points') {
      stats.score = Math.max(0, stats.score - POINTS_PENALTY_WRONG);
    }
    if (settings.failMode !== 'none') {
      view.setMood('sad');
    }
    publishHud();
  }

  function handleInput(event: InputEvent): void {
    if (paused || stopped) return;
    if (event.type === 'click') handleClick(event);
    else handleKey(event);
  }

  function handleTick(remaining: number): void {
    const fraction = settings.roundSeconds > 0 ? remaining / settings.roundSeconds : 0;
    view.setTime(remaining, Math.max(0, Math.min(1, fraction)));
    if (remaining > 0 && remaining <= TIME_TICK_THRESHOLD_S) audio.tick();
  }

  function handleEnd(): void {
    stop();
    onEnd({ ...stats });
  }

  function start(): void {
    if (stopped) return;
    publishHud();
    view.setTime(settings.roundSeconds, 1);
    inputBus.subscribe(handleInput);
    timer = createTimer(settings.roundSeconds, { onTick: handleTick, onEnd: handleEnd });
    timer.start();
    startSpawnLoop();
    startFrameLoop();
  }

  function pause(): void {
    if (paused || stopped) return;
    paused = true;
    pausedAt = performance.now();
    stopSpawnLoop();
    stopFrameLoop();
    timer?.pause();
  }

  function resume(): void {
    if (!paused || stopped) return;
    if (pausedAt !== null) {
      totalPausedMs += performance.now() - pausedAt;
      pausedAt = null;
    }
    paused = false;
    timer?.resume();
    startSpawnLoop();
    startFrameLoop();
  }

  function stop(): void {
    if (stopped) return;
    stopped = true;
    stopFrameLoop();
    stopSpawnLoop();
    timer?.stop();
    timer = null;
    inputBus.unsubscribe(handleInput);
    inputBus.destroy();
    for (const t of targets) t.destroy();
    targets = [];
  }

  function getStats(): EngineStats {
    return { ...stats };
  }

  function getCombo(): number {
    return combo;
  }

  function getRemaining(): number {
    return timer?.getRemaining() ?? 0;
  }

  function isPaused(): boolean {
    return paused;
  }

  function adjustTimer(deltaSeconds: number): void {
    timer?.adjust(deltaSeconds);
  }

  function endNow(): void {
    if (!timer) return;
    timer.adjust(-timer.getRemaining());
  }

  return {
    start,
    pause,
    resume,
    stop,
    getStats,
    getCombo,
    getRemaining,
    isPaused,
    adjustTimer,
    endNow,
  };
}
